import { useEffect, useState } from "react";

import {
    ArrowLeft,
    Download,
    FileText,
    Home,
    Loader,
} from "lucide-react";
import {
    getBreadcrumb,
    getDownloadUrl,
    getFileInfo,
} from "../../utils/api";
import "./PdfViewerPage.css";

export default function PdfViewerPage({
    fileId,
    onBack,
    onNavigateFolder,
}) {
    const id = fileId;

    const [file, setFile] = useState(null);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        let objectUrl = null;

        const loadDocument = async () => {
            try {
                setLoading(true);
                setError(null);

                const fileResponse = await getFileInfo(id);
                const fileData = fileResponse.data;

                setFile(fileData);

                if (
                    fileData.mimeType !== "application/pdf" &&
                    !fileData.name?.toLowerCase().endsWith(".pdf")
                ) {
                    throw new Error("This file is not a PDF");
                }

                if (fileData.parentId) {
                    const breadcrumbResponse = await getBreadcrumb(
                        fileData.parentId,
                    );
                    setBreadcrumbs(breadcrumbResponse.data);
                } else {
                    setBreadcrumbs([]);
                }

                const token = localStorage.getItem("token");

                const response = await fetch(getDownloadUrl(id), {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error("Unable to load PDF");
                }

                const blob = await response.blob();

                objectUrl = URL.createObjectURL(
                    new Blob([blob], {
                        type: "application/pdf",
                    }),
                );

                setPdfUrl(objectUrl);
            } catch (err) {
                if (err.name !== "AbortError") {
                    setError(err.message || "Unable to load document");
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadDocument();

        return () => {
            controller.abort();

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [id]);

    const handleDownload = async () => {
        try {
            const token = localStorage.getItem("token");

            const response = await fetch(getDownloadUrl(id), {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Download failed");
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = file?.name || "document.pdf";

            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);

            URL.revokeObjectURL(url);
        } catch {
            setError("Unable to download document");
        }
    };

    const navigateToFolder = (folderId) => {
        onNavigateFolder(folderId);
    };

    return (
        <div className="pdf-page">
            <header className="pdf-header">
                <div className="pdf-header-left">
                    <button
                        className="pdf-icon-button"
                        onClick={() => onBack()}
                        title="Back"
                    >
                        <ArrowLeft size={19} />
                    </button>

                    <div className="pdf-breadcrumbs">
                        <button
                            className="pdf-breadcrumb"
                            onClick={() => navigateToFolder(null)}
                        >
                            <Home size={14} />
                            My Files
                        </button>

                        {breadcrumbs.map((crumb) => (
                            <div
                                className="pdf-breadcrumb-section"
                                key={crumb._id}
                            >
                                <span className="pdf-breadcrumb-separator">
                                    /
                                </span>

                                <button
                                    className="pdf-breadcrumb"
                                    onClick={() =>
                                        navigateToFolder(crumb._id)
                                    }
                                >
                                    {crumb.name}
                                </button>
                            </div>
                        ))}

                        <span className="pdf-breadcrumb-separator">
                            /
                        </span>

                        <div className="pdf-current-file">
                            <FileText size={15} />
                            <span>{file?.name || "Document"}</span>
                        </div>
                    </div>
                </div>

                <div className="pdf-header-actions">
                    <button
                        className="pdf-download-button"
                        onClick={handleDownload}
                        disabled={!file}
                    >
                        <Download size={16} />
                        Download
                    </button>
                </div>
            </header>

            <main className="pdf-content">
                {loading && (
                    <div className="pdf-loading">
                        <Loader
                            className="pdf-loading-spinner"
                            size={28}
                        />
                        <span>Loading document...</span>
                    </div>
                )}

                {!loading && error && (
                    <div className="pdf-error">
                        <FileText size={42} />
                        <h2>Unable to open document</h2>
                        <p>{error}</p>
                        <button onClick={() => onBack()}>
                            Go back
                        </button>
                    </div>
                )}

                {!loading && !error && pdfUrl && (
                    <iframe
                        className="pdf-frame"
                        src={pdfUrl}
                        title={file?.name || "PDF document"}
                    />
                )}
            </main>
        </div>
    );
}